import { AppLanguage, setLanguage } from './expressions/helpers/locale';
import type { TableValue } from './expressions/helpers/expression-functions';
import {
  executeExpression,
  executeSimpleExpression,
  initialData,
} from './expressions/executor';
import {
  ExpressionFunctionsCachedResult,
  ExpressionFunctionsDto,
  QueryWithParameters,
} from './expressions/types';
import {
  extractQueriesFromExpression,
  setSelectVisitsFunctionInternal,
  distinctQueries,
} from './expressions/parser';
import {
  getFactor,
  isSimpleTimeFormat,
  localFormatDuration,
  roundUsingFormat,
} from './expressions/helpers/date-helper';
import {
  getExpressionFunctionCachedResult,
  NS_DataBlockFields,
} from './expressions/helpers/post-process';
import i18next from 'i18next';
export type { QueryWithParameters, TableValue };
export {
  initialData,
  localFormatDuration,
  isSimpleTimeFormat,
  getFactor,
  roundUsingFormat,
  distinctQueries,
  getExpressionFunctionCachedResult,
};
import * as fs from 'fs';
// !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
// !!! All functions in this file MUST BE ASYNC to be used from backend !!!
// !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!

/*
 * Finds all queries used in passed expression. Mind that queries could be duplicated (e.g. `patient` query in example below)!
 * E.g. expression: `patient()['FirstName'] + ' ' + patient()['LastName'] + ' ' + case()['Number'] + ' ' + lastBlockInCase('PSG')['AHI']
 * will return:
 * [
 *   {
 *      "query":{
 *         "queryType":"MergeFieldQueryType.PatientQuery",
 *         "dataBlockTypeName": null
 *      },
 *      "expressionFunctionName": "patient"
 *   },
 *   {
 *      "query":{
 *         "queryType":"MergeFieldQueryType.PatientQuery",
 *         "dataBlockTypeName": null
 *      },
 *      "expressionFunctionName": "patient"
 *   },
 *   {
 *      "query":{
 *         "queryType":"MergeFieldQueryType.CaseQuery",
 *         "dataBlockTypeName": null
 *      },
 *      "expressionFunctionName": "case"
 *   },
 *   {
 *      "query":{
 *         "queryType":"MergeFieldQueryType.DataBlockQuery",
 *         "dataBlockTypeName": "PSG"
 *      },
 *      "expressionFunctionArgument": "PSG",
 *      "expressionFunctionName": "lastBlockInCase",
 *      "limit": "lastBlockInCase"
 *   }
 * ]
 */
export async function findQueries(
  expression: string
): Promise<QueryWithParameters[]> {
  return await extractQueriesFromExpression(expression);
}

export async function calculateExpressionBackend(
  expression: string,
  /*
   * dataKey is a Key returned from setExpressionFunctionData function.
   * (so, you need to call `setExpressionFunctionData` prior to calling `calculateExpressions`)
   * This is required, because `ExpressionFunctionsCachedResult` has .toString() overridden on certain objects,
   * which can't be passed via serialization from .NET
   */
  dataKey: string
): Promise<any> {
  await initI18nextIfNotYet();
  await i18next.changeLanguage(_language);

  const result = await calculateExpression(expression, dataKey);

  if (Array.isArray(result)) {
    if (result.length === 0) {
      return '';
    } else {
      return result.join(', ');
    }
  }
  return result;
}

let _isIn18NextInitialized = false;
async function initI18nextIfNotYet() {
  if (_isIn18NextInitialized) return;

  const langs = ['en', 'de'];
  await i18next.init({
    ns: ['translation', NS_DataBlockFields],
    supportedLngs: langs,
    fallbackLng: 'en',
    returnObjects: true,
    load: 'languageOnly',
    lowerCaseLng: true,
    interpolation: {
      escapeValue: false, // not needed for react!!
    },
    react: {
      useSuspense: false,
    },
    joinArrays: '\n',
  });

  langs.forEach((lang) => {
    i18next.addResourceBundle(
      lang,
      NS_DataBlockFields,
      JSON.parse(
        fs
          .readFileSync(`./Dictionaries/${lang}/DataBlockFields.json`)
          .toString()
      )
    );
  });

  _isIn18NextInitialized = true;
}

export async function calculateExpression(
  expression: string,
  /*
   * dataKey is a Key returned from setExpressionFunctionData function.
   * (so, you need to call `setExpressionFunctionData` prior to calling `calculateExpressions`)
   * This is required, because `ExpressionFunctionsCachedResult` has .toString() overridden on certain objects,
   * which can't be passed via serialization from .NET
   */
  dataKey: string
): Promise<any> {
  const data = _data[dataKey];
  if (!_data.hasOwnProperty(dataKey)) {
    throw new Error(
      `Data by key '${dataKey}' not found. Please call setExpressionFunctionData first.`
    );
  }
  return executeExpression(expression, data);
}

export function calculateExpressionSync(
  expression: string,
  /*
   * dataKey is a Key returned from setExpressionFunctionData function.
   * (so, you need to call `setExpressionFunctionData` prior to calling `calculateExpressions`)
   * This is required, because `ExpressionFunctionsCachedResult` has .toString() overridden on certain objects,
   * which can't be passed via serialization from .NET
   */
  dataKey: string
): any {
  const data = _data[dataKey];
  if (!_data.hasOwnProperty(dataKey)) {
    throw new Error(
      `Data by key '${dataKey}' not found. Please call setExpressionFunctionData first.`
    );
  }
  return executeExpression(expression, data);
}

export async function calculateExpressions(
  expression: string,
  /*
   * dataKey is a Key returned from setExpressionFunctionData function.
   * (so, you need to call `setExpressionFunctionData` prior to calling `calculateExpressions`)
   * This is required, because `ExpressionFunctionsCachedResult` has .toString() overridden on certain objects,
   * which can't be passed via serialization from .NET
   */
  dataKeys: string[]
) {
  const contexts = dataKeys.map((dataKey) => {
    const data = _data[dataKey];
    if (!data)
      throw new Error(
        `Data by key '${dataKey}' not found. Please call setExpressionFunctionData first.`
      );
    return data;
  });

  return JSON.stringify(
    contexts.map((item) => executeExpression(expression, item))
  );
}

/*
 * Calculates simple expressions like `replaceValues(data['asd'], 'zxc', 'qwe')`.
 * `data` should be provided as a second argument.
 */
export async function calculateSimpleExpressions(
  expression: string,
  /*
   * Data is a regular object (Record<string, any>), which could be passed also as serialized string if used from .NET
   * (there are errors passing arrays from .Net in non-serialized form)
   */
  data: string | Record<string, any>
) {
  const context =
    typeof data === 'string' ? (JSON.parse(data) as Record<string, any>) : data;

  return executeSimpleExpression(expression, context);
}

/*
 * Calculates simple expressions like `replaceValues(data['asd'], 'zxc', 'qwe')`.
 * `data` should be provided as a second argument.
 */
export function calculateSimpleExpressionSync(
  expression: string,
  /*
   * Data is a regular object (Record<string, any>), which could be passed also as serialized string if used from .NET
   * (there are errors passing arrays from .Net in non-serialized form)
   */
  data: string | Record<string, any>
) {
  const context =
    typeof data === 'string' ? (JSON.parse(data) as Record<string, any>) : data;

  return executeSimpleExpression(expression, context);
}

let _language: string;
export async function setLanguageForExpressions(language: AppLanguage) {
  setLanguage(language);
  _language = language;
}

const _data: Record<string, ExpressionFunctionsCachedResult> = {};
export async function setExpressionFunctionData(
  /*
   * data is serialized ExpressionFunctionsDto[]
   */
  data: string
): Promise<string> {
  return setExpressionFunctionDataSync(
    JSON.parse(data) as ExpressionFunctionsDto[]
  );
}
export function setExpressionFunctionDataSync(
  data: ExpressionFunctionsDto[]
): string {
  const key = createId() + '-' + createId() + '-' + createId();
  _data[key] = getExpressionFunctionCachedResult(data);
  setTimeout(() => {
    void removeExpressionFunctionData(key);
  }, 60000);
  return key;
}
export function setExpressionFunctionCachedResultSync(
  data: ExpressionFunctionsCachedResult
): string {
  const key = createId() + '-' + createId() + '-' + createId();
  _data[key] = data;

  // setTimeout(() => {
  //   void removeExpressionFunctionData(key);
  // }, 600000);
  return key;
}

export async function removeExpressionFunctionData(key: string) {
  delete _data[key];
}

/*
 * This is mostly for debug purposes (to check how the data is converted)
 */
export async function getExpressionFunctionData(key: string): Promise<string> {
  return JSON.stringify(_data[key]);
}

export async function echo(name: string) {
  return 'Hi ' + name;
}

export function setSelectVisitsFunction(func: () => Promise<number[]>) {
  setSelectVisitsFunctionInternal(func);
}

function createId() {
  return Math.random().toString(36).substr(2, 9);
}
